import { useEffect, useState, useRef, useCallback, memo } from 'react';
import clsx from 'clsx';

import { computSimpleHashFromData } from '../utils/utils';

import styles from './redlnotrenderableinfo.module.scss';

const FADE_OUT_DURATION = 300; // Match this to the CSS transition duration

const transformData = (data) => {
    return Object.entries(data).reduce((acc, [source, items]) => {
        items.forEach(({ componentName, blocIdx }) => {
            acc[blocIdx] ??= {};
            acc[blocIdx][source] ??= {};
            acc[blocIdx][source][componentName] = (acc[blocIdx][source][componentName] || 0) + 1;
        });
        return acc;
    }, {});
}

const generateGroupedMessages = (transformedData) => {
    return Object.entries(transformedData).map(([blocIdx, sources]) => {
        const totalItemsMissing = Object.values(sources)
            .flatMap((components) => Object.values(components))
            .reduce((sum, count) => sum + count, 0);
        if (blocIdx === 'NODATA') {
            let noDataKeys = getKeysRecursively(sources);
            return `El component ${noDataKeys[1]} no té la font de dades ${noDataKeys[0]} que necessita.`;
        }
        return `Al bloc número ${blocIdx} li ${totalItemsMissing > 1 ? "manquen" : "manca"} ${totalItemsMissing} ${totalItemsMissing > 1 ? "ítems" : "ítem"
            } per mostrar-se correctament.`;
    });
}

const getKeysRecursively = (data) => {
    const keys = [];

    const traverse = (currentObj, parentKey = null) => {
        Object.entries(currentObj).forEach(([key, value]) => {
            keys.push(key);
            if (typeof value === 'object' && value !== null) {
                traverse(value, parentKey ? `${parentKey}.${key}` : key);
            }
        });
    }

    traverse(data);
    return keys;
}

const RedlNotRenderableInfo = ({ pageNotRenderableInfo }) => {
    const [messages, setMessages] = useState([]);
    const [isVisible, setIsVisible] = useState(true);
    const [isFading, setIsFading] = useState(false);
    const prevHashRef = useRef(null);

    useEffect(() => {
        if (!pageNotRenderableInfo) return;

        const currentHash = computSimpleHashFromData(pageNotRenderableInfo);
        if (currentHash === prevHashRef.current) return;

        const transformedData = transformData(pageNotRenderableInfo);
        const updatedMessages = generateGroupedMessages(transformedData);

        setMessages(updatedMessages);
        setIsVisible(updatedMessages.length > 0);

        prevHashRef.current = currentHash;
    }, [pageNotRenderableInfo]);

    const handleClose = useCallback(() => {
        setIsFading(true);
        setTimeout(() => {
            setIsVisible(false);
        }, FADE_OUT_DURATION);
    }, []);

    const containerClass = clsx(styles.redlNotRenderableInfo, {
        [styles.fading]: isFading,
        [styles.notVisible]: !messages || !isVisible
    });

    return (
        <div className={styles.redlNotRenderableInfo__wrapper}>
            <div className={containerClass} role="alert" aria-live="polite" data-testid='container-redlNotRenderableInfo'>
                <div className={styles.redlNotRenderableInfo__header}>
                    <h2 className={styles.redlNotRenderableInfo__title}>
                        <img src='/_resources/icones/avisos/warning_groc.svg' alt='Avís' />
                        ATENCIÓ!
                    </h2>
                    <button className={styles.closeButton} onClick={handleClose} data-testid='btn-close-redlNotRenderableInfo'>
                        <img width="10" src="/_resources/icones/negre/tanca-petit.svg" alt="Tancar" />
                    </button>
                </div>
                <div className={styles.redlMsgContainer}>

                    <ul className={styles.redlMsg}>
                        {messages.map((message, index) => (
                            <li key={index} className={styles.redlNotRenderableInfo__text}>
                                {message}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

function areEqual(prevProps, nextProps) {
    const prevHash = computSimpleHashFromData(prevProps.pageNotRenderableInfo);
    const nextHash = computSimpleHashFromData(nextProps.pageNotRenderableInfo);
    return prevHash === nextHash;
}

RedlNotRenderableInfo.displayName = 'RedlNotRenderableInfo';
export default memo(RedlNotRenderableInfo, areEqual);
